import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ExternalLink, Plus, Search } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';

const API = 'api/replai/v2/cases/';
const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
const ADYEN = 'https://ca-live.adyen.com/ca/ca/accounts/showTx.shtml?txType=Payment&pspReference=';
const STATUS = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-800' },
  waiting_agent: { label: 'Waiting for agent', cls: 'bg-blue-100 text-blue-800' },
  won: { label: 'Won', cls: 'bg-green-100 text-green-800' },
  lost: { label: 'Lost', cls: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-700' },
};
const OPEN = ['pending', 'waiting_agent'];
const SLOW_DAYS = 30;
const EVENT = {
  NOTIFICATION_OF_CHARGEBACK: 'Chargeback notified', REQUEST_FOR_INFORMATION: 'Information requested', CHARGEBACK: 'Chargeback (debited)',
  CHARGEBACK_REVERSED: 'Reversed: won', SECOND_CHARGEBACK: 'Second chargeback', PREARBITRATION_WON: 'Pre-arbitration won',
  PREARBITRATION_LOST: 'Pre-arbitration lost', NOTIFICATION_OF_FRAUD: 'Fraud notice', INFORMATION_SUPPLIED: 'Information supplied',
  DISPUTE_DEFENSE_PERIOD_ENDED: 'Defence period ended',
};

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`rounded-md px-3 py-1.5 text-sm ${active ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
    {children}
  </button>
);

const money = (c) => (c.amount == null ? '–' : `${Number(c.amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })} ${c.currency}`);
// The respond-by day as typed and shown: the Brussels day (stored at noon there), red 3 days before.
const dueDay = (c) => (c.due_at ? new Date(c.due_at).toLocaleDateString('en-CA', { timeZone: 'Europe/Brussels' }) : '');
const dueSoon = (c) => c.due_at && new Date(c.due_at) - new Date() < 3 * 86400000;

const CaseRow = ({ c, lead, agents, onSaved, onRemoved, focused }) => {
  // A link from Buddy's panel (/cases/<id>) opens its case unfolded, in view.
  const [open, setOpen] = useState(!!focused);
  const [note, setNote] = useState(c.note || '');
  const ref = useRef(null);
  useEffect(() => { if (focused && ref.current) ref.current.scrollIntoView({ block: 'center' }); }, [focused]);
  const save = (body) => axiosInstance.post(`${API}${c.id}/`, body).then(({ data }) => onSaved(data))
    .catch((e) => window.alert(e?.response?.data?.error || 'Could not save the case.'));
  const slow = OPEN.includes(c.status) && (c.age_days || 0) >= SLOW_DAYS;
  return (
    <li ref={ref} className={`rounded-lg border bg-[var(--surface)] p-3 ${focused ? 'border-[var(--ink)] ring-1 ring-[var(--ink)]' : 'border-[var(--line)]'}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
        <span className="num w-28 font-semibold">{money(c)}</span>
        <span className="w-20 text-xs uppercase tracking-wide text-[var(--ink-3)]">{c.source === 'adyen' ? 'Card' : c.source === 'paypal' ? 'PayPal' : 'Manual'}{c.kind === 'fraud_notice' ? ' · fraud' : ''}</span>
        <span className="min-w-0 flex-1 truncate">
          <b>{c.customer_name || c.customer_email || 'Customer not found'}</b>
          {c.order && <span className="text-[var(--ink-3)]"> · {c.order}</span>}
          {c.ticket_id && <a href={`${GORGIAS}${c.ticket_id}`} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 hover:underline">ticket <ExternalLink size={11} /></a>}
        </span>
        {lead ? (
          <select value={c.agent || ''} onChange={(e) => save({ agent: e.target.value })} className="rounded-md border border-[var(--line)] bg-[var(--surface)] px-1.5 py-1">
            <option value="">No agent</option>
            {[...new Set([c.agent, ...agents].filter(Boolean))].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        ) : <span className="font-medium">{c.agent}</span>}
        <select value={c.status} onChange={(e) => save({ status: e.target.value })}
          className={`rounded-md px-2 py-1 text-xs font-semibold ${STATUS[c.status]?.cls || ''}`}>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {OPEN.includes(c.status) && c.due_at && (
          <span className={`text-xs ${dueSoon(c) ? 'font-semibold text-red-700' : 'text-[var(--ink-2)]'}`}>
            reply by {new Date(c.due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/Brussels' })}
          </span>
        )}
        <span className={`w-24 text-right text-xs ${slow ? 'font-semibold text-red-700' : 'text-[var(--ink-3)]'}`}>
          {c.age_days != null ? `${c.age_days} days${OPEN.includes(c.status) ? ' open' : ''}` : ''}
        </span>
        <button type="button" onClick={() => setOpen(!open)} className="text-xs text-[var(--ink-2)] underline">{open ? 'Less' : 'Details'}</button>
      </div>
      {open && (
        <div className="mt-3 grid gap-3 border-t border-[var(--line-2)] pt-3 text-sm md:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-semibold text-[var(--ink-3)]">What happened</div>
            <ul className="space-y-0.5">
              {(c.events || []).map((e) => <li key={`${e.psp}-${e.code}`}><span className="text-[var(--ink-3)]">{new Date(e.at).toLocaleDateString('en-GB')}</span> {EVENT[e.code] || e.code}{e.reason ? ` · ${e.reason}` : ''}</li>)}
              {!c.events?.length && <li className="text-[var(--ink-3)]">Added by hand by {c.updated_by}.</li>}
            </ul>
            {c.source === 'adyen' && <a href={`${ADYEN}${c.payment_ref}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs hover:underline">Open in Adyen <ExternalLink size={11} /></a>}
            {c.customer_email && <div className="mt-1 text-xs text-[var(--ink-3)]">{c.customer_email}</div>}
            <label className="mt-2 flex items-center gap-1.5 text-xs text-[var(--ink-3)]">Respond by
              <input type="date" defaultValue={dueDay(c)} onBlur={(e) => e.target.value !== dueDay(c) && save({ due_at: e.target.value })}
                className="rounded-md border border-[var(--line)] px-1.5 py-0.5" />
            </label>
            {lead && (
              <label className="mt-2 flex items-center gap-1.5 text-xs text-[var(--ink-3)]">Opened on
                <input type="date" defaultValue={(c.opened_at || '').slice(0, 10)} onBlur={(e) => e.target.value && save({ opened: e.target.value })}
                  className="rounded-md border border-[var(--line)] px-1.5 py-0.5" />
              </label>
            )}
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold text-[var(--ink-3)]">Notes</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full rounded-md border border-[var(--line)] p-2" />
            <div className="mt-1 flex items-center gap-2">
              <button type="button" disabled={note === (c.note || '')} onClick={() => save({ note })} className="rounded-md border border-[var(--line)] px-2 py-1 text-xs disabled:opacity-50">Save note</button>
              {c.updated_by && <span className="text-xs text-[var(--ink-3)]">last change by {c.updated_by}</span>}
            </div>
            {lead && (
              // Archive any case; delete one added by hand (Geoff, 2026-10-26: duplicates).
              <div className="mt-3 flex items-center gap-2">
                <button type="button" onClick={() => save({ archived: !c.archived }).then(() => onRemoved(c.id, true))}
                  className="rounded-md border border-[var(--line)] px-2 py-1 text-xs">{c.archived ? 'Restore' : 'Archive'}</button>
                {c.manual && (
                  <button type="button" onClick={() => window.confirm('Delete this case for good?')
                    && axiosInstance.delete(`${API}${c.id}/`).then(() => onRemoved(c.id, false))}
                    className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700">Delete</button>)}
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

const AddCase = ({ agents, onAdded }) => {
  const [f, setF] = useState({ source: 'paypal', ticket: '', order: '', amount: '', currency: 'EUR', agent: '', reason: '', opened: '', due_at: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // One case per click: the button waits for the save (Claudia Hagemann got 7).
  const add = () => {
    if (busy) return;
    setBusy(true);
    axiosInstance.post(API, f).then(({ data }) => { if (!data.warning) onAdded(data); setF({ ...f, ticket: '', order: '', amount: '', reason: '', opened: '', due_at: '' }); setErr(data.warning || ''); })
      .catch((e) => setErr(e?.response?.data?.error || 'Could not add the case.'))
      .finally(() => setBusy(false));
  };
  const input = 'rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1.5';
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-[var(--line)] p-3 text-sm">
      <Plus size={15} className="text-[var(--ink-3)]" />
      <select value={f.source} onChange={set('source')} className={input}><option value="paypal">PayPal claim</option><option value="adyen">Card chargeback</option><option value="manual">Other</option></select>
      <input value={f.ticket} onChange={set('ticket')} placeholder="Ticket number" className={`${input} w-36`} />
      <input value={f.order} onChange={set('order')} placeholder="Order #" className={`${input} w-28`} />
      <input value={f.amount} onChange={set('amount')} placeholder="Amount" className={`${input} w-24`} />
      <label className="flex items-center gap-1 text-xs text-[var(--ink-3)]">Opened <input type="date" value={f.opened} onChange={set('opened')} className={input} /></label>
      <label className="flex items-center gap-1 text-xs text-[var(--ink-3)]">Respond by <input type="date" value={f.due_at} onChange={set('due_at')} className={input} /></label>
      <input value={f.currency} onChange={set('currency')} className={`${input} w-16`} />
      <select value={f.agent} onChange={set('agent')} className={input}><option value="">Agent</option>{agents.map((a) => <option key={a} value={a}>{a}</option>)}</select>
      <input value={f.reason} onChange={set('reason')} placeholder="Reason" className={`${input} min-w-[10rem] flex-1`} />
      <button type="button" onClick={add} disabled={busy} className="rounded-md bg-[var(--ink)] px-3 py-1.5 font-medium text-white disabled:opacity-50">{busy ? 'Adding…' : 'Add case'}</button>
      {err && <span className="text-red-700">{err}</span>}
    </div>
  );
};

const CasesV2 = () => {
  // /cases/<id>: the case a Buddy panel button links to, unfolded under "All".
  const { caseId } = useParams();
  const focus = /^\d+$/.test(caseId || '') ? Number(caseId) : null;
  const [d, setD] = useState(null);
  const [tab, setTab] = useState(focus ? 'all' : 'open');
  const [agent, setAgent] = useState('');
  const [q, setQ] = useState('');
  const [src, setSrc] = useState('');
  const [days, setDays] = useState(0);
  const archived = tab === 'archived';
  const load = useCallback(() => axiosInstance.get(API, { params: { days, archived: archived ? 1 : 0, ...(focus ? { case: focus } : {}) } })
    .then(({ data }) => setD(data)).catch(() => setD(false)), [days, archived, focus]);
  useEffect(() => { load(); }, [load]);
  const cases = (d && d.cases) || [];
  const replace = (row) => setD((x) => ({ ...x, cases: x.cases.map((c) => (c.id === row.id ? row : c)) }));
  const agents = useMemo(() => (d ? d.agents.map((a) => a.agent) : []), [d]);
  const needle = q.trim().toLowerCase();
  const inTab = (c) => (archived ? true : tab === 'open' ? OPEN.includes(c.status) : tab === 'slow' ? OPEN.includes(c.status) && (c.age_days || 0) >= SLOW_DAYS
    : tab === 'unassigned' ? !c.agent : tab === 'closed' ? !OPEN.includes(c.status) : true);
  const rows = cases.filter((c) => inTab(c) && (!agent || c.agent === agent) && (!src || c.source === src)
    && (!needle || [c.customer_name, c.customer_email, c.order, String(c.ticket_id || ''), c.payment_ref].join(' ').toLowerCase().includes(needle)));
  const n = (f) => cases.filter(f).length;
  const closed = cases.filter((c) => ['won', 'lost'].includes(c.status));
  const winRate = closed.length ? Math.round((100 * closed.filter((c) => c.status === 'won').length) / closed.length) : null;

  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Cases</h1>
          <div className="flex gap-1">{[[30, '30 days'], [60, '60 days'], [90, '90 days'], [180, '6 months'], [365, '12 months'], [0, 'All']].map(([n, l]) => (
            <Chip key={n} active={days === n} onClick={() => setDays(n)}>{l}</Chip>))}</div>
        </div>
        <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">
          PayPal claims and card chargebacks, linked to the order, ticket and agent. Card chargebacks arrive from Adyen by themselves;
          PayPal claims can be added by hand for now. {d && !d.lead && 'You see the cases counted for you.'}
        </p>
        {d === false && <p className="mt-8 text-[var(--ink-2)]">Could not load. Reload the page to try again.</p>}
        {d == null && <div className="mt-8 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {d && (
          <>
            <section className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
              {[[n((c) => OPEN.includes(c.status)), 'open'], [n((c) => OPEN.includes(c.status) && (c.age_days || 0) >= SLOW_DAYS), `open ${SLOW_DAYS}+ days`],
                [n((c) => c.status === 'won'), 'won'], [winRate == null ? '–' : `${winRate}%`, `won of ${closed.length} closed`]].map(([v, l]) => (
                <div key={l} className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-3">
                  <div className="num text-2xl font-semibold">{v}</div><div className="text-sm text-[var(--ink-2)]">{l}</div>
                </div>
              ))}
            </section>
            {d.lead && d.agents.length > 0 && (
              <section className="mt-4 overflow-x-auto rounded-lg border border-[var(--line)] bg-[var(--surface)]">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-[var(--ink-3)]"><tr><th className="px-3 py-2">Agent</th><th className="px-3 py-2">Open cases</th>
                    <th className="px-3 py-2">Won</th><th className="px-3 py-2">Lost</th><th className="px-3 py-2">Negative reviews ({days ? `${days} days` : '90 days'})</th></tr></thead>
                  <tbody>{d.agents.map((a) => (
                    <tr key={a.agent} onClick={() => setAgent(agent === a.agent ? '' : a.agent)}
                      className={`cursor-pointer border-t border-[var(--line-2)] ${agent === a.agent ? 'bg-[var(--paper)]' : 'hover:bg-[var(--paper)]'}`}>
                      <td className="px-3 py-2 font-medium">{a.agent}</td><td className="num px-3 py-2">{a.open}</td><td className="num px-3 py-2">{a.won}</td>
                      <td className="num px-3 py-2">{a.lost}</td><td className="num px-3 py-2">{a.negative_reviews}</td>
                    </tr>))}</tbody>
                </table>
              </section>
            )}
            {d.lead && <AddCase agents={agents} onAdded={(c) => setD((x) => ({ ...x, cases: [c, ...x.cases] }))} />}
            <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-[var(--line)] pb-3">
              <Chip active={tab === 'open'} onClick={() => setTab('open')}>Open ({n((c) => OPEN.includes(c.status))})</Chip>
              <Chip active={tab === 'slow'} onClick={() => setTab('slow')}>Taking too long ({n((c) => OPEN.includes(c.status) && (c.age_days || 0) >= SLOW_DAYS)})</Chip>
              {d.lead && <Chip active={tab === 'unassigned'} onClick={() => setTab('unassigned')}>No agent ({n((c) => !c.agent)})</Chip>}
              <Chip active={tab === 'closed'} onClick={() => setTab('closed')}>Won or lost ({closed.length})</Chip>
              <Chip active={tab === 'all'} onClick={() => setTab('all')}>All ({archived ? '…' : cases.length})</Chip>
              {d.lead && <Chip active={archived} onClick={() => setTab('archived')}>Archived ({d.archived_count || 0})</Chip>}
              <select value={src} onChange={(e) => setSrc(e.target.value)} className="ml-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm">
                <option value="">Card and PayPal ({cases.length})</option>
                <option value="adyen">Card chargebacks, Adyen ({n((c) => c.source === 'adyen')})</option>
                <option value="paypal">PayPal claims ({n((c) => c.source === 'paypal')})</option>
                <option value="manual">Other ({n((c) => c.source === 'manual')})</option>
              </select>
              {agent && <span className="ml-2 text-sm">Agent: <b>{agent}</b> <button type="button" className="underline" onClick={() => setAgent('')}>clear</button></span>}
              <label className="ml-auto flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5">
                <Search size={15} className="text-[var(--ink-3)]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer, order, ticket" className="w-52 bg-transparent text-sm outline-none" />
              </label>
            </div>
            {focus && !cases.some((c) => c.id === focus) && (
              <p className="mt-4 text-sm text-[var(--ink-2)]">Case {focus} is not in this list: it may be archived, or outside the period chosen.</p>
            )}
            {rows.length ? <ul className="mt-4 space-y-2">{rows.map((c) => <CaseRow key={c.id} c={c} lead={d.lead} agents={agents} onSaved={replace} focused={c.id === focus}
              onRemoved={(id, moved) => setD((x) => ({ ...x, cases: x.cases.filter((y) => y.id !== id),
                archived_count: (x.archived_count || 0) + (moved ? (archived ? -1 : 1) : (archived ? -1 : 0)) }))} />)}</ul>
              : <p className="mt-6 text-[var(--ink-2)]">Nothing here.</p>}
            <p className="mt-6 text-xs text-[var(--ink-3)]">Updated {timeAgo(new Date())}. Won and lost come from Adyen by themselves; set Pending or Waiting for agent by hand.</p>
          </>
        )}
      </div>
    </V2Shell>
  );
};

export default CasesV2;
